import { CloseRounded, DoneRounded } from '@mui/icons-material'
import { Chip, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Skeleton, Stack, Typography } from '@mui/material'
import { memo, useEffect, useState } from 'react'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { IMultipleFlowsConfigs, IPollingMultipleFlows } from '../../../definitions/NodesExecution'
import { useAppDispatch } from '../../../store/Hooks'
import { changeRoute, overwriteRouteParams } from '../../../store/slices/SessionSlice'
import useInvalidate from '../../hooks/useInvalidate'
import { buildQueryKey } from '../../hooks/useQuery'
import { snackbar } from '../../services/SnackbarService'

const SkeletonListItem = () => (
    <ListItem>
        <ListItemIcon>
            <Skeleton variant='circular' width={24} height={24} />
        </ListItemIcon>
        <ListItemText primary={<Skeleton width='60%' />} secondary={<Skeleton width='40%' />} />
    </ListItem>
)

const CustomListItem = ({ status, label, runMode }: { status: 'failed' | 'completed' | 'started' | 'waiting', label: string, runMode: 'full' | 'dry' }) => {
    if (status === 'completed') {
        return (
            <ListItem>
                <ListItemIcon>
                    <DoneRounded color='success' />
                </ListItemIcon>
                <ListItemText primary={label} secondary='Completed' secondaryTypographyProps={{ color: 'var(--color-okay)' }} />
            </ListItem>
        )
    }
    if (status === 'failed') {
        return (
            <ListItem>
                <ListItemIcon>
                    <CloseRounded color='error' />
                </ListItemIcon>
                <ListItemText primary={label} secondary='Error' secondaryTypographyProps={{ color: 'var(--color-bad)' }} />
            </ListItem>
        )
    }
    return (
        <ListItem>
            <ListItemIcon>
                <CircularProgress size='20px' color={runMode === 'dry' ? 'dryRun' : 'primary'} />
            </ListItemIcon>
            <ListItemText primary={label} secondary='Processing...' />
        </ListItem>
    )
}

interface P {
    setOpenModal: (openStartRunModal: boolean) => void
    contractUUIDs: string[]
    execEnvUUIDs: string[]
    config: IMultipleFlowsConfigs
    runMode: 'full' | 'dry'
    setLastStepCompleted: (lastStepCompleted: boolean) => void
    setBlockUI: (blockUI: boolean) => void
    refetch?: () => Promise<void>
}
const POLLING_INTERVAL = 2000

const ProgressStep = ({ setOpenModal, contractUUIDs, execEnvUUIDs, runMode, config, setLastStepCompleted, setBlockUI, refetch = null }: P) => {
    const [pollingData, setPollingData] = useState<IPollingMultipleFlows[]>([])
    const dispatch = useAppDispatch()
    const invalidateSettlements = useInvalidate({ queryKey: buildQueryKey('settlements' + '/*') })
    const invalidateSettlementPrevRuns = useInvalidate({ queryKey: buildQueryKey('getSettlementResults-' + '/*') })

    useEffect(() => {
        let isMounted = true

        const poll = async () => {
            try {
                const result = await FlowApiClient.pollingMultipleFlows(execEnvUUIDs)
                if (!isMounted) return

                setPollingData(result)

                const allDone = result.every(f => f.status === 'completed' || f.status === 'failed')

                if (allDone) {
                    setLastStepCompleted(true)
                    setBlockUI(false)
                    dispatch(overwriteRouteParams(null))
                    setTimeout(async () => {
                        setOpenModal(false)
                        await refetch?.()
                        await invalidateSettlements()
                        await invalidateSettlementPrevRuns()
                        dispatch(changeRoute({ route: 'multirun-result', params: { contractUUIDs, eeUUIDs: execEnvUUIDs, config } }))
                    }, 1000)
                    return
                }

                setTimeout(poll, POLLING_INTERVAL)
            } catch (e) {
                snackbar.show({ severity: 'error', text: e?.message || 'Polling status settlement results error' })
            }
        }

        poll()

        return () => {
            isMounted = false
        }
    }, [execEnvUUIDs])

    return (
        <Stack spacing={3} overflow='hidden' paddingX={2}>
            <Stack flexDirection='row' alignItems='center' gap={1.5}>
                <Typography variant='subtitle1'>Contracts in this run</Typography>
                <Chip size='small' label={execEnvUUIDs.length} color={runMode === 'dry' ? 'dryRun' : 'primary'} />
            </Stack>
            <List sx={{ overflowY: 'auto' }}>
                {pollingData.length === 0
                    ? execEnvUUIDs.map((_, i) => <SkeletonListItem key={i} />)
                    : pollingData.map((p, i) => (
                        <CustomListItem
                            key={i}
                            status={p.status}
                            label={p.name}
                            runMode={runMode}
                        />
                    ))}
            </List>
        </Stack>
    )
}

export default memo(ProgressStep)
